import {
    IWallet,
    WalletBalance,
    SendBitcoinParams,
    SettleParams,
    ArkTransaction,
    ExtendedCoin,
    GetVtxosFilter,
    StorageConfig,
    IReadonlyWallet,
    IReadonlyAssetManager,
    IAssetManager,
    AssetDetails,
    IssuanceParams,
    IssuanceResult,
    ReissuanceParams,
    BurnParams,
    Recipient,
} from "..";
import { SettlementEvent } from "../../providers/ark";
import { createDefaultActivityRegistry, buildActivities, type Activity } from "../activity";
import { hex } from "@scure/base";
import {
    Identity,
    ReadonlyIdentity,
    type SerializedIdentity,
    type LegacySerializedIdentity,
    serializeReadonlyIdentity,
    serializeSigningIdentity,
} from "../../identity";
import { WalletRepository } from "../../repositories/walletRepository";
import { ContractRepository } from "../../repositories/contractRepository";
import { setupServiceWorker } from "../../worker/browser/utils";
import { IndexedDBContractRepository, IndexedDBWalletRepository } from "../../repositories";
import {
    RequestClear,
    RequestCreateContract,
    RequestDeleteContract,
    RequestGetAddress,
    RequestGetBalance,
    RequestGetBoardingAddress,
    RequestGetBoardingUtxos,
    RequestAnnotateVtxos,
    RequestGetContracts,
    RequestGetContractsWithVtxos,
    RequestGetContractSyncState,
    RequestGetStatus,
    RequestGetSpendablePaths,
    RequestGetTransactionHistory,
    RequestGetVtxos,
    RequestInitWallet,
    RequestIsContractManagerWatching,
    RequestRefreshVtxos,
    RequestRefreshOutpoints,
    RequestReloadWallet,
    RequestSendBitcoin,
    RequestSettle,
    ResponseSettle,
    ResponseSettleEvent,
    RequestUpdateContract,
    ResponseAnnotateVtxos,
    ResponseGetAddress,
    ResponseGetBalance,
    ResponseGetBoardingAddress,
    ResponseGetBoardingUtxos,
    ResponseGetContracts,
    ResponseGetContractsWithVtxos,
    ResponseGetContractSyncState,
    ResponseGetStatus,
    ResponseGetSpendablePaths,
    ResponseGetTransactionHistory,
    ResponseGetVtxos,
    ResponseIsContractManagerWatching,
    ResponseReloadWallet,
    ResponseSendBitcoin,
    ResponseUpdateContract,
    ResponseCreateContract,
    ResponseContractEvent,
    WalletUpdaterRequest,
    WalletUpdaterResponse,
    RequestGetAllSpendingPaths,
    ResponseGetAllSpendingPaths,
    RequestSend,
    ResponseSend,
    RequestGetAssetDetails,
    ResponseGetAssetDetails,
    RequestIssue,
    ResponseIssue,
    RequestReissue,
    ResponseReissue,
    RequestBurn,
    ResponseBurn,
    RequestDelegate,
    ResponseDelegate,
    RequestGetDelegateInfo,
    ResponseGetDelegateInfo,
    RequestRecoverVtxos,
    ResponseRecoverVtxos,
    ResponseRecoverVtxosEvent,
    RequestGetRecoverableBalance,
    ResponseGetRecoverableBalance,
    RequestGetExpiringVtxos,
    ResponseGetExpiringVtxos,
    RequestRenewVtxos,
    ResponseRenewVtxos,
    ResponseRenewVtxosEvent,
    RequestGetExpiredBoardingUtxos,
    ResponseGetExpiredBoardingUtxos,
    RequestSweepExpiredBoardingUtxos,
    ResponseSweepExpiredBoardingUtxos,
    RequestMigrateDeprecatedSignerVtxos,
    ResponseMigrateDeprecatedSignerVtxos,
    ResponseMigrateDeprecatedSignerVtxosEvent,
    RequestGetDeprecatedSignerStatus,
    ResponseGetDeprecatedSignerStatus,
    deserializeMigrationReport,
    deserializeDeprecatedSignerReport,
    RequestRestoreWallet,
    DEFAULT_MESSAGE_TAG,
    deserializeAggregateError,
    isSerializedAggregateError,
} from "./wallet-message-handler";
import type {
    Contract,
    ContractEventCallback,
    ContractWithVtxos,
    GetContractsFilter,
    PathSelection,
} from "../../contracts";
import type {
    ContractSyncState,
    CreateContractParams,
    GetAllSpendingPathsOptions,
    GetSpendablePathsOptions,
    IContractManager,
    RefreshVtxosOptions,
    ScanResult,
} from "../../contracts/contractManager";
import type { ContractState } from "../../contracts/types";
import type { IDelegateManager } from "../delegate";
import type {
    IVtxoManager,
    MigrateDeprecatedSignerOptions,
    DeprecatedSignerMigrationReport,
    DeprecatedSignerReport,
    RenewVtxosOptions,
    SettlementConfig,
} from "../vtxo-manager";
import type { ContractWatcherConfig } from "../../contracts/contractWatcher";
import type { DelegateInfo } from "../../providers/delegate";
import { getRandomId } from "../utils";
import type { VirtualCoin } from "..";
import {
    isMessageBusInitializingError,
    isMessageBusNotInitializedError,
    ServiceWorkerTimeoutError,
} from "../../worker/errors";
import { getArkadeServerUrl, type ProviderConnectionState } from "../wallet";
import { normalizeVtxo, type NormalizedExtendedVirtualCoin } from "../vtxo";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Bounded backoff for waiting out an in-flight init: ~100ms, 200ms, … capped at
// 2s, so a stuck init still surfaces an error instead of hanging the caller.
const INIT_WAIT_BACKOFF_CAP_MS = 2_000;
const MAX_INIT_WAITS = 8;
const initWaitBackoffMs = (attempt: number) =>
    Math.min(100 * 2 ** attempt, INIT_WAIT_BACKOFF_CAP_MS);

type RequestType = WalletUpdaterRequest["type"];

export type MessageTimeouts = Partial<Record<RequestType, number>>;

export type ServiceWorkerWalletMode = "auto" | "static" | "hd";

export const DEFAULT_MESSAGE_TIMEOUTS: Readonly<Record<RequestType, number>> = {
    // Fast reads — fail quickly
    GET_ADDRESS: 10_000,
    GET_BALANCE: 10_000,
    GET_BOARDING_ADDRESS: 10_000,
    GET_STATUS: 10_000,
    GET_CONTRACT_SYNC_STATE: 10_000,
    GET_DELEGATE_INFO: 10_000,
    IS_CONTRACT_MANAGER_WATCHING: 10_000,

    // Medium reads — may involve indexer queries
    GET_VTXOS: 20_000,
    GET_BOARDING_UTXOS: 20_000,
    GET_TRANSACTION_HISTORY: 20_000,
    GET_CONTRACTS: 20_000,
    GET_CONTRACTS_WITH_VTXOS: 20_000,
    ANNOTATE_VTXOS: 20_000,
    GET_SPENDABLE_PATHS: 20_000,
    GET_ALL_SPENDING_PATHS: 20_000,
    GET_ASSET_DETAILS: 20_000,
    GET_EXPIRING_VTXOS: 20_000,
    GET_EXPIRED_BOARDING_UTXOS: 20_000,
    GET_RECOVERABLE_BALANCE: 20_000,
    GET_DEPRECATED_SIGNER_STATUS: 20_000,
    RELOAD_WALLET: 20_000,

    // Transactions — need more headroom.
    // SETTLE / RECOVER_VTXOS / RENEW_VTXOS go through the streaming path and
    // are treated as long-running on both sides of the bus: the values below
    // are retained only for type completeness and are never enforced.
    SEND_BITCOIN: 50_000,
    SEND: 50_000,
    SETTLE: 50_000,
    ISSUE: 50_000,
    REISSUE: 50_000,
    BURN: 50_000,
    DELEGATE: 50_000,
    RECOVER_VTXOS: 50_000,
    RENEW_VTXOS: 50_000,
    SWEEP_EXPIRED_BOARDING_UTXOS: 50_000,
    // Streaming/long-running like RENEW_VTXOS (rotation + settle); the value is
    // kept for type completeness and is never enforced as an inactivity deadline.
    MIGRATE_DEPRECATED_SIGNER_VTXOS: 50_000,
    // RESTORE_WALLET is a streaming/long-running path (sendMessageWithEvents)
    // like SETTLE; the value here is kept for type completeness and is never
    // enforced as an inactivity deadline.
    RESTORE_WALLET: 50_000,

    // Misc writes
    INIT_WALLET: 30_000,
    CLEAR: 10_000,
    SIGN_TRANSACTION: 30_000,
    CREATE_CONTRACT: 30_000,
    UPDATE_CONTRACT: 30_000,
    DELETE_CONTRACT: 10_000,
    REFRESH_VTXOS: 30_000,
    REFRESH_OUTPOINTS: 30_000,
};

const DEDUPABLE_REQUEST_TYPES: ReadonlySet<string> = new Set([
    "GET_ADDRESS",
    "GET_BALANCE",
    "GET_BOARDING_ADDRESS",
    "GET_BOARDING_UTXOS",
    "GET_STATUS",
    "GET_TRANSACTION_HISTORY",
    "IS_CONTRACT_MANAGER_WATCHING",
    "GET_DELEGATE_INFO",
    "GET_RECOVERABLE_BALANCE",
    "GET_EXPIRED_BOARDING_UTXOS",
    "GET_DEPRECATED_SIGNER_STATUS",
    "GET_VTXOS",
    "GET_CONTRACTS",
    "GET_CONTRACTS_WITH_VTXOS",
    "ANNOTATE_VTXOS",
    "GET_SPENDABLE_PATHS",
    "GET_ALL_SPENDING_PATHS",
    "GET_ASSET_DETAILS",
    "GET_EXPIRING_VTXOS",
    "RELOAD_WALLET",
]);

function getRequestDedupKey(request: WalletUpdaterRequest): string {
    const { id, tag, ...rest } = request;
    return JSON.stringify(rest);
}

function isSigningCapable(identity: Identity | ReadonlyIdentity): identity is Identity {
    const candidate = identity as Partial<Identity>;
    return (
        typeof candidate.signMessage === "function" &&
        typeof candidate.sign === "function" &&
        typeof candidate.signerSession === "function"
    );
}

class ServiceWorkerReadonlyAssetManager implements IReadonlyAssetManager {
    constructor(
        protected readonly sendMessage: (
            msg: WalletUpdaterRequest,
        ) => Promise<WalletUpdaterResponse>,
        protected readonly messageTag: string,
    ) {}

    async getAssetDetails(assetId: string): Promise<AssetDetails> {
        const message: RequestGetAssetDetails = {
            tag: this.messageTag,
            type: "GET_ASSET_DETAILS",
            id: getRandomId(),
            payload: { assetId },
        };
        const response = await this.sendMessage(message);
        return (response as ResponseGetAssetDetails).payload.assetDetails;
    }
}

class ServiceWorkerAssetManager extends ServiceWorkerReadonlyAssetManager implements IAssetManager {
    async issue(params: IssuanceParams): Promise<IssuanceResult> {
        const message: RequestIssue = {
            tag: this.messageTag,
            type: "ISSUE",
            id: getRandomId(),
            payload: { params },
        };
        const response = await this.sendMessage(message);
        return (response as ResponseIssue).payload.result;
    }

    async reissue(params: ReissuanceParams): Promise<string> {
        const message: RequestReissue = {
            tag: this.messageTag,
            type: "REISSUE",
            id: getRandomId(),
            payload: { params },
        };
        const response = await this.sendMessage(message);
        return (response as ResponseReissue).payload.txid;
    }

    async burn(params: BurnParams): Promise<string> {
        const message: RequestBurn = {
            tag: this.messageTag,
            type: "BURN",
            id: getRandomId(),
            payload: { params },
        };
        const response = await this.sendMessage(message);
        return (response as ResponseBurn).payload.txid;
    }
}

/**
 * Service Worker-based wallet implementation for browser environments.
 *
 * This wallet uses a service worker as a backend to handle wallet logic,
 * providing secure key storage and transaction signing in web applications.
 * The service worker runs in a separate thread and can persist data between
 * browser sessions.
 *
 * @example
 * ```typescript
 * // SIMPLE: Recommended approach
 * const wallet = await ServiceWorkerWallet.setup({
 *   serviceWorkerPath: '/service-worker.js',
 *   arkServerUrl: 'https://arkade.computer',
 *   identity: MnemonicIdentity.fromMnemonic('abandon abandon...')
 * });
 *
 * // ADVANCED: Manual setup with service worker control
 * const serviceWorker = await setupServiceWorker("/service-worker.js");
 * const wallet = await ServiceWorkerWallet.create({
 *   serviceWorker,
 *   arkServerUrl: 'https://arkade.computer',
 *   identity: MnemonicIdentity.fromMnemonic('abandon abandon...')
 * });
 *
 * // Use like any other wallet
 * const address = await wallet.getAddress();
 * const balance = await wallet.getBalance();
 * ```
 */
interface ServiceWorkerWalletOptions {
    /** Optional Arkade server public key used to construct and validate Arkade addresses. */
    arkServerPublicKey?: string;
    /**
     * Base URL of the Arkade server.
     *
     * @deprecated Provide an explicit provider via the worker config instead.
     * URL-based configuration will be removed in a future major version.
     */
    arkServerUrl?: string;
    /**
     * Optional override for the indexer URL.
     *
     * @deprecated Provide an explicit provider via the worker config instead.
     */
    indexerUrl?: string;
    /**
     * Optional override for the Esplora API URL.
     *
     * @deprecated Provide an explicit provider via the worker config instead.
     */
    esploraUrl?: string;
    /**
     * Repository-backed storage configuration overrides.
     * Defaults to IndexedDB if unset.
     */
    storage?: StorageConfig;
    /** Identity used to derive addresses and optionally sign operations. */
    identity: ReadonlyIdentity | Identity;
    /** Optional delegation service URL. */
    delegateUrl?: string;
    /** @deprecated alias for @see ServiceWorkerWalletOptions.delegateUrl */
    delegatorUrl?: string;
    /**
     * Override the default tag used for messages sent to and received from the service worker.
     * @see DEFAULT_MESSAGE_TAG
     */
    walletUpdaterTag?: string;
    /** Timeout used while bootstrapping the message bus inside the service worker. */
    messageBusTimeoutMs?: number;
    /** Optional settlement configuration forwarded to the worker wallet. */
    settlementConfig?: SettlementConfig | false;
    /**
     * Receive-address strategy forwarded to the worker wallet.
     *
     * Service workers can only receive serializable configuration, so the
     * descriptor-provider object form accepted by `Wallet.create()` is not
     * supported here.
     */
    walletMode?: ServiceWorkerWalletMode;
    /** Optional contract watcher configuration forwarded to the worker wallet. */
    watcherConfig?: Partial<Omit<ContractWatcherConfig, "indexerProvider">>;
    /**
     * HD look-ahead window forwarded to the worker wallet. Only takes effect
     * together with `walletMode: 'hd'`.
     *
     * @see WalletConfig.lookAheadWindow
     */
    lookAheadWindow?: number;
    /**
     * Per-request timeout overrides for wallet-updater messages.
     * @see DEFAULT_MESSAGE_TIMEOUTS
     */
    messageTimeouts?: MessageTimeouts;
}

/**
 * Options for creating a service-worker wallet with an existing worker instance.
 *
 * @see ServiceWorkerReadonlyWallet.create
 * @see ServiceWorkerWallet.create
 */
export type ServiceWorkerWalletCreateOptions = ServiceWorkerWalletOptions & {
    /** Existing service worker instance used for messaging. */
    serviceWorker: ServiceWorker;
};

/**
 * Options for registering a service worker and then creating a wallet around it.
 *
 * @see ServiceWorkerReadonlyWallet.setup
 * @see ServiceWorkerWallet.setup
 */
export type ServiceWorkerWalletSetupOptions = ServiceWorkerWalletOptions & {
    /** Path to the service worker script to register. */
    serviceWorkerPath: string;
    /** Timeout while waiting for the service worker to activate. */
    serviceWorkerActivationTimeoutMs?: number;
};

type MessageBusInitConfig = {
    wallet: SerializedIdentity | LegacySerializedIdentity;
    arkServer: {
        url: string;
        publicKey?: string;
    };
    delegateUrl?: string;
    /** @deprecated alias for @see MessageBusInitConfig.delegateUrl */
    delegatorUrl?: string;
    indexerUrl?: string;
    esploraUrl?: string;
    timeoutMs?: number;
    settlementConfig?: SettlementConfig | false;
    walletMode?: ServiceWorkerWalletMode;
    watcherConfig?: Partial<Omit<ContractWatcherConfig, "indexerProvider">>;
    lookAheadWindow?: number;
    messageTimeouts?: Record<string, number>;
};

const initializeMessageBus = (
    serviceWorker: ServiceWorker,
    config: MessageBusInitConfig,
    timeoutMs = 2000,
) => {
    const initCmd = {
        tag: "INITIALIZE_MESSAGE_BUS",
        id: getRandomId(),
        config: { ...config, timeoutMs },
    };

    return new Promise<void>((resolve, reject) => {
        const cleanup = () => {
            navigator.serviceWorker.removeEventListener("message", onMessage);
            clearTimeout(timeoutId);
        };

        const onMessage = (event: any) => {
            const response = event.data;
            if (response?.id !== initCmd.id) return;
            cleanup();
            if (response.error) {
                reject(response.error);
            } else {
                resolve();
            }
        };

        const timeoutId = setTimeout(() => {
            cleanup();
            reject(new ServiceWorkerTimeoutError("MessageBus timed out"));
        }, timeoutMs);

        navigator.serviceWorker.addEventListener("message", onMessage);
        serviceWorker.postMessage(initCmd);
    });
};

export class ServiceWorkerReadonlyWallet implements IReadonlyWallet {
    public readonly walletRepository: WalletRepository;
    public readonly contractRepository: ContractRepository;
    public readonly identity: ReadonlyIdentity;
    readonly activity = createDefaultActivityRegistry();
    private readonly _readonlyAssetManager: IReadonlyAssetManager;
    protected initConfig: MessageBusInitConfig | null = null;
    protected initWalletPayload: RequestInitWallet["payload"] | null = null;
    protected messageBusTimeoutMs?: number;
    protected messageTimeouts: Record<RequestType, number> = DEFAULT_MESSAGE_TIMEOUTS as Record<
        RequestType,
        number
    >;
    // Denormalized from options so buildInitConfig() can rebuild the init
    // envelope on demand for SDK-factory-created wallets. `create()` sets
    // these immediately after construction.
    protected arkServerUrl?: string;
    protected arkServerPublicKey?: string;
    protected delegateUrl?: string;
    /** @deprecated alias for @see ServiceWorkerReadonlyWallet.delegateUrl */
    protected delegatorUrl?: string;
    protected indexerUrl?: string;
    protected esploraUrl?: string;
    protected watcherConfig?: Partial<Omit<ContractWatcherConfig, "indexerProvider">>;
    protected settlementConfig?: SettlementConfig | false;
    private reinitPromise: Promise<void> | null = null;
    private pingPromise: Promise<void> | null = null;
    private inflightRequests = new Map<string, Promise<WalletUpdaterResponse>>();

    get assetManager(): IReadonlyAssetManager {
        return this._readonlyAssetManager;
    }

    protected constructor(
        public readonly serviceWorker: ServiceWorker,
        identity: ReadonlyIdentity,
        walletRepository: WalletRepository,
        contractRepository: ContractRepository,
        protected readonly messageTag: string,
    ) {
        this.identity = identity;
        this.walletRepository = walletRepository;
        this.contractRepository = contractRepository;
        this._readonlyAssetManager = new ServiceWorkerReadonlyAssetManager(
            (msg) => this.sendMessage(msg),
            messageTag,
        );
    }

    private getTimeoutForRequest(request: WalletUpdaterRequest): number {
        return this.messageTimeouts[request.type] ?? 30_000;
    }

    /**
     * Create a readonly service-worker wallet bound to an already-registered worker.
     *
     * @param options - Service worker, identity, and backend configuration
     * @returns Initialized readonly service-worker wallet
     * @throws Error if service-worker initialization fails
     */
    static async create(
        options: ServiceWorkerWalletCreateOptions,
    ): Promise<ServiceWorkerReadonlyWallet> {
        const walletRepository =
            options.storage?.walletRepository ?? new IndexedDBWalletRepository();

        const contractRepository =
            options.storage?.contractRepository ?? new IndexedDBContractRepository();

        const messageTag = options.walletUpdaterTag ?? DEFAULT_MESSAGE_TAG;

        // Create the wallet instance
        const wallet = new ServiceWorkerReadonlyWallet(
            options.serviceWorker,
            options.identity,
            walletRepository,
            contractRepository,
            messageTag,
        );

        const serializedWallet = await serializeReadonlyIdentity(options.identity);

        // INIT_WALLET retains the legacy `key` payload for wire compatibility
        // with older workers; the current handler does not read it.
        const publicKey = await options.identity.compressedPublicKey().then(hex.encode);
        const initWalletPayload = {
            key: { publicKey },
            arkServerUrl: getArkadeServerUrl(options),
            arkServerPublicKey: options.arkServerPublicKey,
            delegateUrl: options.delegateUrl || options.delegatorUrl,
            // Keep the deprecated field populated so pre-#519 service workers
            // (which only read delegatorUrl) keep delegating until they activate
            // a newer version.
            delegatorUrl: options.delegateUrl || options.delegatorUrl,
        };

        // Precompute the merged timeout map so page-side waiting and
        // worker-side enforcement are derived from the same source.
        const messageTimeouts = options.messageTimeouts
            ? ({
                  ...DEFAULT_MESSAGE_TIMEOUTS,
                  ...options.messageTimeouts,
              } as Record<RequestType, number>)
            : (DEFAULT_MESSAGE_TIMEOUTS as Record<RequestType, number>);

        const busInitConfig: MessageBusInitConfig = {
            wallet: serializedWallet,
            arkServer: {
                url: getArkadeServerUrl(options),
                publicKey: options.arkServerPublicKey,
            },
            delegateUrl: options.delegateUrl || options.delegatorUrl,
            // Keep the deprecated field populated so pre-#519 service workers
            // (which only read delegatorUrl) keep delegating until they activate
            // a newer version.
            delegatorUrl: options.delegateUrl || options.delegatorUrl,
            indexerUrl: options.indexerUrl,
            esploraUrl: options.esploraUrl,
            watcherConfig: options.watcherConfig,
            messageTimeouts,
        };

        // Bootstrap the MessageBus in the service worker
        await initializeMessageBus(
            options.serviceWorker,
            { ...busInitConfig, timeoutMs: options.messageBusTimeoutMs },
            options.messageBusTimeoutMs,
        );

        // Initialize the wallet handler
        const initMessage: RequestInitWallet = {
            tag: messageTag,
            type: "INIT_WALLET",
            id: getRandomId(),
            payload: initWalletPayload,
        };

        await wallet.sendMessage(initMessage);

        // Persist the full init config (including messageTimeouts) so
        // reinitialize() re-sends the same map to a restarted worker.
        wallet.initConfig = busInitConfig;
        wallet.initWalletPayload = initWalletPayload;
        wallet.messageBusTimeoutMs = options.messageBusTimeoutMs;
        wallet.messageTimeouts = messageTimeouts;

        // Refuse to return a wallet bound to a different identity than the
        // worker ended up with (e.g. a stale/queued init rebinding it).
        await wallet.assertWorkerIdentityMatches();

        return wallet;
    }

    /**
     * Simplified setup method that handles service worker registration
     * and wallet initialization automatically.
     *
     * @see ServiceWorkerReadonlyWallet.create
     *
     * @example
     * ```typescript
     * const wallet = await ServiceWorkerReadonlyWallet.setup({
     *   serviceWorkerPath: '/service-worker.js',
     *   arkServerUrl: 'https://arkade.computer',
     *   identity: ReadonlySingleKey.fromPublicKey('your_public_key_hex')
     * });
     * ```
     */
    static async setup(
        options: ServiceWorkerWalletSetupOptions,
    ): Promise<ServiceWorkerReadonlyWallet> {
        // Register and setup the service worker
        const serviceWorker = await setupServiceWorker({
            path: options.serviceWorkerPath,
            activationTimeoutMs: options.serviceWorkerActivationTimeoutMs,
        });

        // Use the existing create method
        return await ServiceWorkerReadonlyWallet.create({
            ...options,
            serviceWorker,
        });
    }

    private sendMessageDirect(
        request: WalletUpdaterRequest,
        timeoutMs: number,
    ): Promise<WalletUpdaterResponse> {
        return new Promise((resolve, reject) => {
            const cleanup = () => {
                clearTimeout(timeoutId);
                navigator.serviceWorker.removeEventListener("message", messageHandler);
            };

            const timeoutId = setTimeout(() => {
                cleanup();
                reject(
                    new ServiceWorkerTimeoutError(
                        `Service worker message timed out (${request.type})`,
                    ),
                );
            }, timeoutMs);

            const messageHandler = (event: MessageEvent<WalletUpdaterResponse>) => {
                const response = event.data;
                if (request.id !== response.id) {
                    return;
                }

                cleanup();
                if (response.error) {
                    reject(response.error);
                } else {
                    resolve(response);
                }
            };

            navigator.serviceWorker.addEventListener("message", messageHandler);
            this.serviceWorker.postMessage(request);
        });
    }

    // Like sendMessageDirect but supports streaming responses: intermediate
    // messages are forwarded via onEvent while the promise resolves on the
    // first response for which isComplete returns true. No inactivity deadline:
    // settlement-class flows surrender control to remote peers and can sit
    // idle for long stretches between protocol events. Service-worker death
    // is detected out-of-band via concurrent short requests that surface
    // MESSAGE_BUS_NOT_INITIALIZED.
    private sendMessageStreaming(
        request: WalletUpdaterRequest,
        onEvent: (response: WalletUpdaterResponse) => void,
        isComplete: (response: WalletUpdaterResponse) => boolean,
    ): Promise<WalletUpdaterResponse> {
        return new Promise((resolve, reject) => {
            const cleanup = () => {
                navigator.serviceWorker.removeEventListener("message", messageHandler);
            };

            const messageHandler = (event: MessageEvent<WalletUpdaterResponse>) => {
                const response = event.data;
                if (request.id !== response.id) return;

                if (response.error) {
                    cleanup();
                    reject(response.error);
                    return;
                }

                if (isComplete(response)) {
                    cleanup();
                    resolve(response);
                } else {
                    onEvent(response);
                }
            };

            navigator.serviceWorker.addEventListener("message", messageHandler);
            this.serviceWorker.postMessage(request);
        });
    }

    protected async sendMessage(request: WalletUpdaterRequest): Promise<WalletUpdaterResponse> {
        if (!DEDUPABLE_REQUEST_TYPES.has(request.type)) {
            return this.sendMessageWithRetry(request);
        }

        const key = getRequestDedupKey(request);
        const existing = this.inflightRequests.get(key);
        if (existing) return existing;

        const promise = this.sendMessageWithRetry(request).finally(() => {
            this.inflightRequests.delete(key);
        });
        this.inflightRequests.set(key, promise);
        return promise;
    }

    private pingServiceWorker(): Promise<void> {
        if (this.pingPromise) return this.pingPromise;

        this.pingPromise = new Promise<void>((resolve, reject) => {
            const pingId = getRandomId();

            const cleanup = () => {
                clearTimeout(timeoutId);
                navigator.serviceWorker.removeEventListener("message", onMessage);
            };

            const timeoutId = setTimeout(() => {
                cleanup();
                reject(new ServiceWorkerTimeoutError("Service worker ping timed out"));
            }, 2_000);

            const onMessage = (event: MessageEvent) => {
                if (event.data?.id === pingId && event.data?.tag === "PONG") {
                    cleanup();
                    resolve();
                }
            };

            navigator.serviceWorker.addEventListener("message", onMessage);
            this.serviceWorker.postMessage({
                id: pingId,
                tag: "PING",
            });
        }).finally(() => {
            this.pingPromise = null;
        });

        return this.pingPromise;
    }

    // send a message, retrying up to 2 times if the service worker was
    // killed and restarted by the OS (mobile browsers do this aggressively)
    protected async sendMessageWithRetry(
        request: WalletUpdaterRequest,
        withEvents?: {
            onEvent: (response: WalletUpdaterResponse) => void;
            isComplete: (response: WalletUpdaterResponse) => boolean;
        },
    ): Promise<WalletUpdaterResponse> {
        if (this.initConfig) {
            try {
                await this.pingServiceWorker();
            } catch {
                await this.reinitialize();
            }
        }

        const timeoutMs = this.getTimeoutForRequest(request);
        const maxRetries = 2;
        for (let attempt = 0, initWaits = 0; ; attempt++) {
            try {
                if (withEvents) {
                    return await this.sendMessageStreaming(
                        request,
                        withEvents.onEvent,
                        withEvents.isComplete,
                    );
                }
                return await this.sendMessageDirect(request, timeoutMs);
            } catch (error: any) {
                // If init is already in flight in the worker, wait for it
                if (isMessageBusInitializingError(error)) {
                    if (initWaits >= MAX_INIT_WAITS) throw error;
                    await sleep(initWaitBackoffMs(initWaits++));
                    attempt--;
                    continue;
                }
                if (!isMessageBusNotInitializedError(error) || attempt >= maxRetries) {
                    throw error;
                }

                await this.reinitialize();
            }
        }
    }

    /**
     * Produce a serialized envelope for the wallet's identity. The base
     * class always emits a readonly envelope; `ServiceWorkerWallet`
     * overrides to emit a signing envelope.
     */
    protected async serializeIdentity(): Promise<SerializedIdentity> {
        return serializeReadonlyIdentity(this.identity);
    }

    /**
     * Return the cached init config, or rebuild one from live instance
     * state when the cache was never populated. Recovery path for
     * SDK-factory-created wallets; manual constructor bypasses do not
     * retain enough state here and will hit the "never initialized" throw.
     */
    protected async buildInitConfig(): Promise<MessageBusInitConfig> {
        if (this.initConfig) return this.initConfig;
        if (!this.arkServerUrl) {
            throw new Error("Cannot re-initialize: wallet was not initialized via the SDK factory");
        }
        const wallet = await this.serializeIdentity();
        this.initConfig = {
            wallet,
            arkServer: {
                url: this.arkServerUrl,
                publicKey: this.arkServerPublicKey,
            },
            delegateUrl: this.delegateUrl || this.delegatorUrl,
            // Keep the deprecated field populated so pre-#519 service workers
            // (which only read delegatorUrl) keep delegating until they activate
            // a newer version.
            delegatorUrl: this.delegateUrl || this.delegatorUrl,
            indexerUrl: this.indexerUrl,
            esploraUrl: this.esploraUrl,
            watcherConfig: this.watcherConfig,
            settlementConfig: this.settlementConfig,
        };
        return this.initConfig;
    }

    /** Minimal INIT_WALLET payload used on reinitialize when the cache is gone. */
    protected buildInitWalletPayload(): RequestInitWallet["payload"] {
        if (this.initWalletPayload) return this.initWalletPayload;
        if (!this.arkServerUrl) {
            throw new Error("Cannot re-initialize: wallet was not initialized via the SDK factory");
        }
        this.initWalletPayload = {
            // `key` is deprecated and ignored by the current handler.
            key: {},
            arkServerUrl: this.arkServerUrl,
            arkServerPublicKey: this.arkServerPublicKey,
        };
        return this.initWalletPayload;
    }

    private async reinitialize(): Promise<void> {
        if (this.reinitPromise) return this.reinitPromise;

        this.reinitPromise = (async () => {
            const config = await this.buildInitConfig();
            const payload = this.buildInitWalletPayload();

            await initializeMessageBus(this.serviceWorker, config, this.messageBusTimeoutMs);

            const initMessage: RequestInitWallet = {
                tag: this.messageTag,
                type: "INIT_WALLET",
                id: getRandomId(),
                payload,
            };

            await this.sendMessageDirect(initMessage, this.getTimeoutForRequest(initMessage));
            await this.assertWorkerIdentityMatches();
        })().finally(() => {
            this.reinitPromise = null;
        });

        return this.reinitPromise;
    }

    /**
     * Verify the worker is bound to this wallet's identity before the SDK hands
     * back (or recovers) a usable wallet object.
     *
     * Compares the stable baseline identity key (`identity.xOnlyPublicKey()`) to the one reported
     * by the worker via GET_STATUS.
     */
    protected async assertWorkerIdentityMatches(): Promise<void> {
        const expected = hex.encode(await this.identity.xOnlyPublicKey());

        const message: RequestGetStatus = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_STATUS",
        };
        const response = (await this.sendMessageDirect(
            message,
            this.getTimeoutForRequest(message),
        )) as ResponseGetStatus;

        const workerKey = response.payload.xOnlyPublicKey;
        if (!workerKey) {
            throw new Error("Service worker identity mismatch: worker did not report an identity");
        }
        const actual = hex.encode(workerKey);
        if (actual !== expected) {
            throw new Error(
                `Service worker identity mismatch: expected ${expected}, got ${actual}`,
            );
        }
    }

    /** This tells the service worker to wipe all locally persisted wallet data. */
    async clear(): Promise<void> {
        const message: RequestClear = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "CLEAR",
        };
        await this.sendMessage(message);
    }

    async getAddress(): Promise<string> {
        const message: RequestGetAddress = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_ADDRESS",
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetAddress).payload.address;
        } catch (error) {
            throw new Error(`Failed to get address: ${error}`);
        }
    }

    async getBoardingAddress(): Promise<string> {
        const message: RequestGetBoardingAddress = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_BOARDING_ADDRESS",
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetBoardingAddress).payload.address;
        } catch (error) {
            throw new Error(`Failed to get boarding address: ${error}`);
        }
    }

    async getBalance(): Promise<WalletBalance> {
        const message: RequestGetBalance = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_BALANCE",
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetBalance).payload;
        } catch (error) {
            throw new Error(`Failed to get balance: ${error}`);
        }
    }

    async getBoardingUtxos(): Promise<ExtendedCoin[]> {
        const message: RequestGetBoardingUtxos = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_BOARDING_UTXOS",
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetBoardingUtxos).payload.utxos;
        } catch (error) {
            throw new Error(`Failed to get boarding UTXOs: ${error}`);
        }
    }

    /**
     * Return service-worker wallet status, including connectivity and sync state.
     *
     * @returns Current service-worker wallet status payload including `walletInitalized` and `xOnlyPublicKey`
     */
    async getStatus(): Promise<ResponseGetStatus["payload"]> {
        const message: RequestGetStatus = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_STATUS",
        };
        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetStatus).payload;
        } catch (error) {
            throw new Error(`Failed to get status: ${error}`);
        }
    }

    /**
     * Wallet-level provider-connection freshness, delegated to the worker via
     * `GET_STATUS`. Async by necessity (the worker boundary is asynchronous); no
     * synchronous variant is offered because it would have the same transport
     * mismatch as the contract-manager proxy's cached `getSyncState()`.
     */
    async getProviderConnectionState(): Promise<ProviderConnectionState> {
        const { providerConnectionState } = await this.getStatus();
        if (!providerConnectionState) {
            throw new Error("Worker did not report provider connection state");
        }
        return providerConnectionState;
    }

    async getActivityHistory(): Promise<Activity[]> {
        return buildActivities(await this.getTransactionHistory(), this.activity.all());
    }

    async getTransactionHistory(): Promise<ArkTransaction[]> {
        const message: RequestGetTransactionHistory = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_TRANSACTION_HISTORY",
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetTransactionHistory).payload.transactions;
        } catch (error) {
            throw new Error(`Failed to get transaction history: ${error}`);
        }
    }

    async getVtxos(filter?: GetVtxosFilter): Promise<NormalizedExtendedVirtualCoin[]> {
        const message: RequestGetVtxos = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "GET_VTXOS",
            payload: { filter },
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseGetVtxos).payload.vtxos.map(normalizeVtxo);
        } catch (error) {
            throw new Error(`Failed to get vtxos: ${error}`);
        }
    }

    /**
     * Trigger a wallet reload inside the service worker.
     *
     * @returns `true` when the wallet was reloaded
     */
    async reload(): Promise<boolean> {
        const message: RequestReloadWallet = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "RELOAD_WALLET",
        };
        try {
            const response = await this.sendMessage(message);
            return (response as ResponseReloadWallet).payload.reloaded;
        } catch (error) {
            throw new Error(`Failed to reload wallet: ${error}`);
        }
    }

    async getContractManager(): Promise<IContractManager> {
        const wallet = this;

        const sendContractMessage = async <T extends WalletUpdaterRequest>(
            message: T,
        ): Promise<WalletUpdaterResponse> => {
            return wallet.sendMessage(message as WalletUpdaterRequest);
        };

        const messageTag = this.messageTag;

        // Page-side cache of the worker-owned ContractManager's sync health. The
        // worker remains the authoritative owner (see the file-level ownership
        // rules); this proxy is a cached VIEW refreshed at call boundaries, never
        // a second source of truth. Synchronous IContractManager.getSyncState()
        // reads this cache; async operations refresh it.
        const fetchSyncState = async (): Promise<ContractSyncState> => {
            const message: RequestGetContractSyncState = {
                type: "GET_CONTRACT_SYNC_STATE",
                id: getRandomId(),
                tag: messageTag,
            };
            const response = await sendContractMessage(message);
            return (response as ResponseGetContractSyncState).payload.syncState;
        };
        // Start degraded/unknown — NOT online — so a probe that times out, hits
        // an old worker, or errors is never reported as fresh. Only a successful
        // probe establishes a real state; after that, a failed probe preserves
        // the last known good value (best-effort) rather than fabricating one.
        const UNKNOWN_STATE: ContractSyncState = {
            mode: "degraded",
            reason: "contract sync state unavailable from the worker",
        };
        let syncState: ContractSyncState = UNKNOWN_STATE;
        let everProbed = false;
        const refreshSyncState = async (): Promise<void> => {
            // Best-effort: a failed diagnostics refresh must never mask the
            // caller's operation result/error, and must never throw.
            try {
                syncState = await fetchSyncState();
                everProbed = true;
            } catch {
                // Keep the last known good state only once we've had one; before
                // any successful probe, stay degraded/unknown instead of online.
                if (!everProbed) {
                    syncState = UNKNOWN_STATE;
                }
            }
        };
        // Seed the cache before returning the proxy (best-effort — never blocks
        // or fails construction on a diagnostics hiccup).
        await refreshSyncState();

        const manager: IContractManager = {
            async createContract(params: CreateContractParams): Promise<Contract> {
                const message: RequestCreateContract = {
                    type: "CREATE_CONTRACT",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: params,
                };
                try {
                    const response = await sendContractMessage(message);
                    // Hydration may have degraded the worker manager (or cleared
                    // a prior degradation) — refresh the cached view.
                    await refreshSyncState();
                    return (response as ResponseCreateContract).payload.contract;
                } catch (e) {
                    throw new Error("Failed to create contract");
                }
            },

            async getContracts(filter?: GetContractsFilter): Promise<Contract[]> {
                const message: RequestGetContracts = {
                    type: "GET_CONTRACTS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { filter },
                };
                try {
                    const response = await sendContractMessage(message);
                    return (response as ResponseGetContracts).payload.contracts;
                } catch (e) {
                    throw new Error("Failed to get contracts");
                }
            },

            async getContractsWithVtxos(filter: GetContractsFilter): Promise<ContractWithVtxos[]> {
                const message: RequestGetContractsWithVtxos = {
                    type: "GET_CONTRACTS_WITH_VTXOS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { filter },
                };
                try {
                    const response = await sendContractMessage(message);
                    // A best-effort sync ran on the worker; it may have degraded
                    // to repository data or recovered — refresh the cached view.
                    await refreshSyncState();
                    return (response as ResponseGetContractsWithVtxos).payload.contracts;
                } catch (e) {
                    throw new Error("Failed to get contracts with vtxos");
                }
            },

            getSyncState(): ContractSyncState {
                // Synchronous read of the page-side cache, seeded at proxy
                // construction and refreshed after operations that can move the
                // worker manager between online and degraded.
                return syncState;
            },

            async annotateVtxos(vtxos: VirtualCoin[]): Promise<NormalizedExtendedVirtualCoin[]> {
                if (vtxos.length === 0) return [];
                const message: RequestAnnotateVtxos = {
                    type: "ANNOTATE_VTXOS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { vtxos },
                };
                try {
                    const response = await sendContractMessage(message);
                    return (response as ResponseAnnotateVtxos).payload.vtxos.map(normalizeVtxo);
                } catch (e) {
                    throw new Error("Failed to annotate vtxos");
                }
            },

            async updateContract(
                script: string,
                updates: Partial<Omit<Contract, "script" | "createdAt">>,
            ): Promise<Contract> {
                const message: RequestUpdateContract = {
                    type: "UPDATE_CONTRACT",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { script, updates },
                };
                try {
                    const response = await sendContractMessage(message);
                    return (response as ResponseUpdateContract).payload.contract;
                } catch (e) {
                    throw new Error("Failed to update contract");
                }
            },

            async setContractState(script: string, state: ContractState): Promise<void> {
                const message: RequestUpdateContract = {
                    type: "UPDATE_CONTRACT",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { script, updates: { state } },
                };
                try {
                    await sendContractMessage(message);
                    return;
                } catch (e) {
                    throw new Error("Failed to update contract state");
                }
            },

            async deleteContract(script: string): Promise<void> {
                const message: RequestDeleteContract = {
                    type: "DELETE_CONTRACT",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { script },
                };
                try {
                    await sendContractMessage(message);
                    return;
                } catch (e) {
                    throw new Error("Failed to delete contract");
                }
            },

            async getSpendablePaths(options: GetSpendablePathsOptions): Promise<PathSelection[]> {
                const message: RequestGetSpendablePaths = {
                    type: "GET_SPENDABLE_PATHS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { options },
                };
                try {
                    const response = await sendContractMessage(message);
                    return (response as ResponseGetSpendablePaths).payload.paths;
                } catch (e) {
                    throw new Error("Failed to get spendable paths");
                }
            },

            async getAllSpendingPaths(
                options: GetAllSpendingPathsOptions,
            ): Promise<PathSelection[]> {
                const message: RequestGetAllSpendingPaths = {
                    type: "GET_ALL_SPENDING_PATHS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { options },
                };
                try {
                    const response = await sendContractMessage(message);
                    return (response as ResponseGetAllSpendingPaths).payload.paths;
                } catch (e) {
                    throw new Error("Failed to get all spending paths");
                }
            },

            onContractEvent(callback: ContractEventCallback): () => void {
                const messageHandler = (event: MessageEvent) => {
                    const response = event.data as WalletUpdaterResponse;
                    if (response.type !== "CONTRACT_EVENT") {
                        return;
                    }
                    if (response.tag !== messageTag) {
                        return;
                    }
                    callback((response as ResponseContractEvent).payload.event);
                };

                navigator.serviceWorker.addEventListener("message", messageHandler);

                return () => {
                    navigator.serviceWorker.removeEventListener("message", messageHandler);
                };
            },

            async refreshVtxos(opts?: RefreshVtxosOptions): Promise<void> {
                const message: RequestRefreshVtxos = {
                    type: "REFRESH_VTXOS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: opts,
                };
                // Explicit remote refresh: it surfaces its own retryable error to
                // the caller, but the proxy cache is refreshed either way so it
                // doesn't stay stale after a thrown provider failure.
                try {
                    await sendContractMessage(message);
                } finally {
                    await refreshSyncState();
                }
            },

            async refreshOutpoints(outpoints: { txid: string; vout: number }[]): Promise<void> {
                const message: RequestRefreshOutpoints = {
                    type: "REFRESH_OUTPOINTS",
                    id: getRandomId(),
                    tag: messageTag,
                    payload: { outpoints },
                };
                try {
                    await sendContractMessage(message);
                } finally {
                    await refreshSyncState();
                }
            },

            scanContracts(): Promise<ScanResult> {
                // `scanContracts` takes a `materialize(index)` callback,
                // which cannot cross the service-worker postMessage boundary
                // (functions are not structured-cloneable). Service-worker
                // wallets must drive recovery through the worker's own
                // restore message protocol, not this proxy method.
                return Promise.reject(
                    new Error(
                        "scanContracts is not available on the service-worker " +
                            "contract-manager proxy: its materialize() callback " +
                            "cannot be sent across the worker message boundary. " +
                            "Use the wallet's restore entrypoint instead.",
                    ),
                );
            },

            refillLookAhead(): Promise<void> {
                // The look-ahead is wired into the inner Wallet the worker
                // owns, and every refill trigger fires there. A page-side call
                // has nothing to schedule.
                return Promise.resolve();
            },

            async isWatching(): Promise<boolean> {
                const message: RequestIsContractManagerWatching = {
                    type: "IS_CONTRACT_MANAGER_WATCHING",
                    id: getRandomId(),
                    tag: messageTag,
                };
                try {
                    const response = await sendContractMessage(message);
                    return (response as ResponseIsContractManagerWatching).payload.isWatching;
                } catch (e) {
                    throw new Error("Failed to check if contract manager is watching");
                }
            },

            dispose(): void {
                return;
            },

            [Symbol.dispose](): void {
                // no-op
                return;
            },
        };

        return manager;
    }
}

export class ServiceWorkerWallet extends ServiceWorkerReadonlyWallet implements IWallet {
    public readonly walletRepository: WalletRepository;
    public readonly contractRepository: ContractRepository;
    public readonly identity: Identity;
    private readonly _assetManager: IAssetManager;
    private readonly hasDelegate: boolean;

    protected constructor(
        public readonly serviceWorker: ServiceWorker,
        identity: Identity,
        walletRepository: WalletRepository,
        contractRepository: ContractRepository,
        messageTag: string,
        hasDelegate: boolean,
    ) {
        super(serviceWorker, identity, walletRepository, contractRepository, messageTag);
        this.identity = identity;
        this.walletRepository = walletRepository;
        this.contractRepository = contractRepository;
        this._assetManager = new ServiceWorkerAssetManager(
            (msg) => this.sendMessage(msg),
            messageTag,
        );
        this.hasDelegate = hasDelegate;
    }

    get assetManager(): IAssetManager {
        return this._assetManager;
    }

    protected async serializeIdentity(): Promise<SerializedIdentity> {
        return serializeSigningIdentity(this.identity);
    }

    static async create(options: ServiceWorkerWalletCreateOptions): Promise<ServiceWorkerWallet> {
        // Same guard the inner `Wallet.create` applies, run here so a bad value
        // fails at the call site instead of inside the worker.
        if (
            options.lookAheadWindow !== undefined &&
            (!Number.isInteger(options.lookAheadWindow) || options.lookAheadWindow <= 0)
        ) {
            throw new Error(
                `lookAheadWindow must be a positive integer (got ${String(options.lookAheadWindow)})`,
            );
        }

        const walletRepository =
            options.storage?.walletRepository ?? new IndexedDBWalletRepository();

        const contractRepository =
            options.storage?.contractRepository ?? new IndexedDBContractRepository();

        if (!isSigningCapable(options.identity)) {
            throw new Error(
                "ServiceWorkerWallet.create() requires a signing Identity; got a ReadonlyIdentity",
            );
        }
        const identity: Identity = options.identity;
        const serializedWallet = serializeSigningIdentity(identity);

        const messageTag = options.walletUpdaterTag ?? DEFAULT_MESSAGE_TAG;

        // Create the wallet instance
        const wallet = new ServiceWorkerWallet(
            options.serviceWorker,
            identity,
            walletRepository,
            contractRepository,
            messageTag,
            !!(options.delegateUrl || options.delegatorUrl),
        );

        // INIT_WALLET retains the legacy `key` payload for wire compatibility
        // with older workers; the current handler does not read it, and only
        // SingleKey-style identities can populate it. Kept optional so seed /
        // mnemonic identities simply omit it.
        const legacyPrivateKey =
            serializedWallet.type === "single-key" ? serializedWallet.privateKey : null;
        const initWalletPayload = {
            key: legacyPrivateKey ? { privateKey: legacyPrivateKey } : {},
            arkServerUrl: getArkadeServerUrl(options),
            arkServerPublicKey: options.arkServerPublicKey,
            delegateUrl: options.delegateUrl || options.delegatorUrl,
            // Keep the deprecated field populated so pre-#519 service workers
            // (which only read delegatorUrl) keep delegating until they activate
            // a newer version.
            delegatorUrl: options.delegateUrl || options.delegatorUrl,
        };

        // Precompute the merged timeout map so page-side waiting and
        // worker-side enforcement are derived from the same source.
        const messageTimeouts = options.messageTimeouts
            ? ({
                  ...DEFAULT_MESSAGE_TIMEOUTS,
                  ...options.messageTimeouts,
              } as Record<RequestType, number>)
            : (DEFAULT_MESSAGE_TIMEOUTS as Record<RequestType, number>);

        const busInitConfig: MessageBusInitConfig = {
            wallet: serializedWallet,
            arkServer: {
                url: getArkadeServerUrl(options),
                publicKey: options.arkServerPublicKey,
            },
            delegateUrl: options.delegateUrl || options.delegatorUrl,
            // Keep the deprecated field populated so pre-#519 service workers
            // (which only read delegatorUrl) keep delegating until they activate
            // a newer version.
            delegatorUrl: options.delegateUrl || options.delegatorUrl,
            indexerUrl: options.indexerUrl,
            esploraUrl: options.esploraUrl,
            settlementConfig: options.settlementConfig,
            walletMode: options.walletMode,
            watcherConfig: options.watcherConfig,
            lookAheadWindow: options.lookAheadWindow,
            messageTimeouts,
        };

        await initializeMessageBus(
            options.serviceWorker,
            { ...busInitConfig, timeoutMs: options.messageBusTimeoutMs },
            options.messageBusTimeoutMs,
        );
        // Initialize the service worker with the config
        const initMessage: RequestInitWallet = {
            tag: messageTag,
            type: "INIT_WALLET",
            id: getRandomId(),
            payload: initWalletPayload,
        };

        // Initialize the service worker
        await wallet.sendMessage(initMessage);

        // Persist the full init config (including messageTimeouts) so
        // reinitialize() re-sends the same map to a restarted worker.
        wallet.initConfig = busInitConfig;
        wallet.initWalletPayload = initWalletPayload;
        wallet.messageBusTimeoutMs = options.messageBusTimeoutMs;
        wallet.messageTimeouts = messageTimeouts;

        // Refuse to return a wallet bound to a different identity than the
        // worker ended up with (e.g. a stale/queued init rebinding it).
        await wallet.assertWorkerIdentityMatches();

        return wallet;
    }

    /**
     * Simplified setup method that handles service worker registration
     * and wallet initialization automatically.
     *
     * @example
     * ```typescript
     * const wallet = await ServiceWorkerWallet.setup({
     *   serviceWorkerPath: '/service-worker.js',
     *   arkServerUrl: 'https://arkade.computer',
     *   identity: MnemonicIdentity.fromMnemonic('abandon abandon...')
     * });
     * ```
     */
    static async setup(options: ServiceWorkerWalletSetupOptions): Promise<ServiceWorkerWallet> {
        // Register and setup the service worker
        const serviceWorker = await setupServiceWorker({
            path: options.serviceWorkerPath,
            activationTimeoutMs: options.serviceWorkerActivationTimeoutMs,
        });

        // Use the existing create method
        return ServiceWorkerWallet.create({
            ...options,
            serviceWorker,
        });
    }

    async sendBitcoin(params: SendBitcoinParams): Promise<string> {
        const message: RequestSendBitcoin = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "SEND_BITCOIN",
            payload: params,
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseSendBitcoin).payload.txid;
        } catch (error) {
            throw new Error(`Failed to send bitcoin: ${error}`);
        }
    }

    async settle(
        params?: SettleParams,
        callback?: (event: SettlementEvent) => void,
    ): Promise<string> {
        const message: RequestSettle = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "SETTLE",
            payload: { params },
        };

        try {
            const response = await this.sendMessageWithRetry(message, {
                onEvent: (resp) => callback?.((resp as ResponseSettleEvent).payload),
                isComplete: (resp) => resp.type === "SETTLE_SUCCESS",
            });
            return (response as ResponseSettle).payload.txid;
        } catch (error) {
            throw new Error(`Settlement failed: ${error}`);
        }
    }

    /**
     * Explicitly recover this wallet's contracts and balance on a fresh repo.
     * Mirrors {@link Wallet.restore} but drives the scan inside the service
     * worker — the materialize() callback used by `scanContracts` cannot
     * cross the postMessage boundary, so the entire flow runs worker-side
     * and only the gapLimit / outcome cross the wire.
     *
     * Uses the streaming send path so the bus deadline does not race a
     * long indexer-bound scan. AggregateError thrown by the worker is
     * reconstructed here so callers can inspect `.errors`.
     */
    async restore(opts?: { gapLimit?: number }): Promise<void> {
        const message: RequestRestoreWallet = {
            id: getRandomId(),
            tag: this.messageTag,
            type: "RESTORE_WALLET",
            payload: opts ?? {},
        };

        try {
            await this.sendMessageWithRetry(message, {
                onEvent: () => {},
                isComplete: (resp) => resp.type === "RESTORE_WALLET_SUCCESS",
            });
        } catch (error: unknown) {
            if (isSerializedAggregateError(error)) {
                throw deserializeAggregateError(error);
            }
            throw error;
        }
    }

    async send(...recipients: [Recipient, ...Recipient[]]): Promise<string> {
        const message: RequestSend = {
            tag: this.messageTag,
            type: "SEND",
            id: getRandomId(),
            payload: { recipients },
        };

        try {
            const response = await this.sendMessage(message);
            return (response as ResponseSend).payload.txid;
        } catch (error) {
            throw new Error(`Send failed: ${error}`);
        }
    }

    async getDelegateManager(): Promise<IDelegateManager | undefined> {
        if (!this.hasDelegate) {
            return undefined;
        }

        const wallet = this;
        const messageTag = this.messageTag;

        const manager: IDelegateManager = {
            async delegate(vtxos, destination, delegateAt?) {
                const message: RequestDelegate = {
                    tag: messageTag,
                    type: "DELEGATE",
                    id: getRandomId(),
                    payload: {
                        vtxoOutpoints: vtxos.map((v) => ({
                            txid: v.txid,
                            vout: v.vout,
                        })),
                        destination,
                        delegateAt: delegateAt?.getTime(),
                    },
                };

                try {
                    const response = await wallet.sendMessage(message);
                    const payload = (response as ResponseDelegate).payload;
                    return {
                        delegated: payload.delegated,
                        failed: payload.failed.map((f) => ({
                            outpoints: f.outpoints,
                            error: f.error,
                        })),
                    };
                } catch (error) {
                    throw new Error(`Delegation failed: ${error}`);
                }
            },

            async getDelegateInfo(): Promise<DelegateInfo> {
                const message: RequestGetDelegateInfo = {
                    type: "GET_DELEGATE_INFO",
                    id: getRandomId(),
                    tag: messageTag,
                };
                try {
                    const response = await wallet.sendMessage(message);
                    return (response as ResponseGetDelegateInfo).payload.info;
                } catch (e) {
                    throw new Error("Failed to get delegate info");
                }
            },
        };

        return manager;
    }

    /** @deprecated alias for @see ServiceWorkerWallet.getDelegateManager */
    async getDelegatorManager() {
        return await this.getDelegateManager();
    }

    async getVtxoManager(): Promise<IVtxoManager> {
        const wallet = this;
        const messageTag = this.messageTag;

        const manager: IVtxoManager = {
            async recoverVtxos(eventCallback?: (event: SettlementEvent) => void): Promise<string> {
                const message: RequestRecoverVtxos = {
                    tag: messageTag,
                    type: "RECOVER_VTXOS",
                    id: getRandomId(),
                };
                try {
                    const response = await wallet.sendMessageWithRetry(message, {
                        onEvent: (resp) =>
                            eventCallback?.((resp as ResponseRecoverVtxosEvent).payload),
                        isComplete: (resp) => resp.type === "RECOVER_VTXOS_SUCCESS",
                    });
                    return (response as ResponseRecoverVtxos).payload.txid;
                } catch (e) {
                    throw new Error(`Failed to recover vtxos: ${e}`);
                }
            },

            async getRecoverableBalance() {
                const message: RequestGetRecoverableBalance = {
                    tag: messageTag,
                    type: "GET_RECOVERABLE_BALANCE",
                    id: getRandomId(),
                };
                try {
                    const response = await wallet.sendMessage(message);
                    const payload = (response as ResponseGetRecoverableBalance).payload;
                    return {
                        recoverable: BigInt(payload.recoverable),
                        subdust: BigInt(payload.subdust),
                        includesSubdust: payload.includesSubdust,
                        vtxoCount: payload.vtxoCount,
                    };
                } catch (e) {
                    throw new Error(`Failed to get recoverable balance: ${e}`);
                }
            },

            async getExpiringVtxos(thresholdMs?) {
                const message: RequestGetExpiringVtxos = {
                    tag: messageTag,
                    type: "GET_EXPIRING_VTXOS",
                    id: getRandomId(),
                    payload: { thresholdMs },
                };
                try {
                    const response = await wallet.sendMessage(message);
                    return (response as ResponseGetExpiringVtxos).payload.vtxos.map(normalizeVtxo);
                } catch (e) {
                    throw new Error(`Failed to get expiring vtxos: ${e}`);
                }
            },

            async renewVtxos(
                eventCallback?: (event: SettlementEvent) => void,
                options?: RenewVtxosOptions,
            ): Promise<string> {
                const message: RequestRenewVtxos = {
                    tag: messageTag,
                    type: "RENEW_VTXOS",
                    id: getRandomId(),
                    payload: options,
                };
                try {
                    const response = await wallet.sendMessageWithRetry(message, {
                        onEvent: (resp) =>
                            eventCallback?.((resp as ResponseRenewVtxosEvent).payload),
                        isComplete: (resp) => resp.type === "RENEW_VTXOS_SUCCESS",
                    });
                    return (response as ResponseRenewVtxos).payload.txid;
                } catch (e) {
                    throw new Error(`Failed to renew vtxos: ${e}`);
                }
            },

            async getExpiredBoardingUtxos() {
                const message: RequestGetExpiredBoardingUtxos = {
                    tag: messageTag,
                    type: "GET_EXPIRED_BOARDING_UTXOS",
                    id: getRandomId(),
                };
                try {
                    const response = await wallet.sendMessage(message);
                    return (response as ResponseGetExpiredBoardingUtxos).payload.utxos;
                } catch (e) {
                    throw new Error(`Failed to get expired boarding utxos: ${e}`);
                }
            },

            async sweepExpiredBoardingUtxos(): Promise<string> {
                const message: RequestSweepExpiredBoardingUtxos = {
                    tag: messageTag,
                    type: "SWEEP_EXPIRED_BOARDING_UTXOS",
                    id: getRandomId(),
                };
                try {
                    const response = await wallet.sendMessage(message);
                    return (response as ResponseSweepExpiredBoardingUtxos).payload.txid;
                } catch (e) {
                    throw new Error(`Failed to sweep expired boarding utxos: ${e}`);
                }
            },

            async migrateDeprecatedSignerVtxos(
                options?: MigrateDeprecatedSignerOptions,
            ): Promise<DeprecatedSignerMigrationReport> {
                const message: RequestMigrateDeprecatedSignerVtxos = {
                    tag: messageTag,
                    type: "MIGRATE_DEPRECATED_SIGNER_VTXOS",
                    id: getRandomId(),
                };
                try {
                    const response = await wallet.sendMessageWithRetry(message, {
                        onEvent: (resp) =>
                            options?.eventCallback?.(
                                (resp as ResponseMigrateDeprecatedSignerVtxosEvent).payload,
                            ),
                        isComplete: (resp) =>
                            resp.type === "MIGRATE_DEPRECATED_SIGNER_VTXOS_SUCCESS",
                    });
                    return deserializeMigrationReport(
                        (response as ResponseMigrateDeprecatedSignerVtxos).payload.report,
                    );
                } catch (e) {
                    throw new Error(`Failed to migrate deprecated-signer vtxos: ${e}`);
                }
            },

            async getDeprecatedSignerStatus(): Promise<DeprecatedSignerReport[]> {
                const message: RequestGetDeprecatedSignerStatus = {
                    tag: messageTag,
                    type: "GET_DEPRECATED_SIGNER_STATUS",
                    id: getRandomId(),
                };
                try {
                    const response = await wallet.sendMessage(message);
                    return (response as ResponseGetDeprecatedSignerStatus).payload.signers.map(
                        deserializeDeprecatedSignerReport,
                    );
                } catch (e) {
                    throw new Error(`Failed to get deprecated-signer status: ${e}`);
                }
            },

            async dispose(): Promise<void> {
                return;
            },
        };

        return manager;
    }
}
